import { useMemo, useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export type Column<TRow> = {
  key: string;
  header: string;
  align?: "left" | "right";
  sticky?: boolean;
  render: (row: TRow) => React.ReactNode;
  sortAccessor?: (row: TRow) => number | string | null;
  sortable?: boolean;
};

type Props<TRow extends { merchantId: string }> = {
  rows: TRow[];
  columns: Column<TRow>[];
  defaultSort: { columnKey: string; direction: "asc" | "desc" };
  maxHeightClass?: string;
  emptyMessage?: string;
};

const isSortable = <TRow,>(col: Column<TRow>) =>
  col.sortable !== false && col.sortAccessor !== undefined;

const compareValues = (
  a: number | string | null,
  b: number | string | null,
  direction: "asc" | "desc"
): number => {
  // Nulls always sort last regardless of direction
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  if (typeof a === "string" && typeof b === "string") {
    const cmp = a.localeCompare(b);
    return direction === "asc" ? cmp : -cmp;
  }
  if (a < b) return direction === "asc" ? -1 : 1;
  if (a > b) return direction === "asc" ? 1 : -1;
  return 0;
};

const SortableMetricTable = <TRow extends { merchantId: string }>({
  rows,
  columns,
  defaultSort,
  maxHeightClass = "max-h-[60vh]",
  emptyMessage = "Sin datos para mostrar.",
}: Props<TRow>) => {
  const [sortColumnKey, setSortColumnKey] = useState<string>(defaultSort.columnKey);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(defaultSort.direction);

  const handleSort = (columnKey: string) => {
    if (sortColumnKey === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumnKey(columnKey);
      setSortDirection("desc");
    }
  };

  const sortedRows = useMemo(() => {
    const col = columns.find((c) => c.key === sortColumnKey);
    if (!col || !col.sortAccessor) return rows;
    const accessor = col.sortAccessor;
    return [...rows].sort((a, b) => compareValues(accessor(a), accessor(b), sortDirection));
  }, [rows, columns, sortColumnKey, sortDirection]);

  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    if (sortColumnKey !== columnKey)
      return <ArrowUpDown className="w-3 h-3 ml-1 inline text-gray-400" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline text-blue-500" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline text-blue-500" />
    );
  };

  if (rows.length === 0) {
    return (
      <Card className="border border-gray-200">
        <CardContent className="p-6 text-center text-sm text-gray-500">
          {emptyMessage}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-gray-200 overflow-hidden">
      <div className={`overflow-x-auto overflow-y-auto ${maxHeightClass}`}>
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              {columns.map((col) => {
                const sortable = isSortable(col);
                const alignClass = col.align === "left" ? "text-left" : "text-right";
                const stickyClass = col.sticky
                  ? "sticky left-0 bg-gray-50 z-20"
                  : "";
                const cursorClass = sortable ? "cursor-pointer hover:bg-gray-100" : "";
                return (
                  <th
                    key={col.key}
                    className={`px-4 py-3 text-xs font-semibold text-gray-600 ${alignClass} ${stickyClass} ${cursorClass}`}
                    onClick={sortable ? () => handleSort(col.key) : undefined}
                  >
                    {col.header}
                    {sortable && <SortIcon columnKey={col.key} />}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr
                key={row.merchantId}
                className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
              >
                {columns.map((col) => {
                  const alignClass = col.align === "left" ? "text-left" : "text-right";
                  const stickyClass = col.sticky ? "sticky left-0 bg-white" : "";
                  return (
                    <td
                      key={col.key}
                      className={`px-4 py-3 text-sm tabular-nums text-gray-900 ${alignClass} ${stickyClass}`}
                    >
                      {col.render(row)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default SortableMetricTable;
